/**
 * Shared builder that turns a flat list of filterable columns into "questions".
 *
 * A question is the 1st dimension. It is either:
 *   - a `single` column (its label is the question, with one option = itself), or
 *   - a `group` of columns sharing a question stem (the 2nd dimension are the
 *     options, e.g. "Salesforce", "HubSpot"; the 3rd dimension are values).
 *
 * Used by both the Add-filter modal (to list questions/groups) and the chip
 * grid (to collapse a group's member columns into one chip). Keeping a single
 * builder guarantees the grouping shown in the modal matches the grouping used
 * to render and remove chips.
 */
import type { AudienceImportSegmentFiltersColumnGroup } from '@digital-twins/shared-types'
import {
  CATEGORY_ORDER,
  type SegmentationCategory,
} from '@/types/components/audiences/segmentation-mapping'
import {
  buildPhase0Grouping,
  groupColumnsBySharedStem,
  splitStemAndItem,
} from '@/lib/utils/column-labels'

export interface SegmentFilterColumnInput {
  sourceColumn: string
  /** Resolved display label (canonical / custom / mapping label). */
  normalizedLabel: string
  /** Original column label from the uploaded file (used for stem grouping). */
  originalLabel: string
  category: SegmentationCategory | null
  sampleValues: Array<string>
  valueLabels?: Record<string, string>
  valueMap?: Record<string, string>
  disabled?: boolean
  disabledReason?: string
}

/** One option (member column) of a question — the 2nd dimension. */
export interface SegmentFilterOption {
  sourceColumn: string
  /** Item label, e.g. "Salesforce"; equals the question label for flat columns. */
  optionLabel: string
  originalLabel: string
  sampleValues: Array<string>
  valueLabels?: Record<string, string>
  valueMap?: Record<string, string>
  disabled?: boolean
  disabledReason?: string
}

export interface SegmentFilterQuestion {
  /** Stable id: `c:<sourceColumn>` for singles, `g:<stem>:<firstMember>` for groups. */
  id: string
  label: string
  category: string
  kind: 'single' | 'group'
  /** For `single` there is exactly one option (the column itself). */
  options: Array<SegmentFilterOption>
}

function toOption(col: SegmentFilterColumnInput, optionLabel: string): SegmentFilterOption {
  return {
    sourceColumn: col.sourceColumn,
    optionLabel,
    originalLabel: col.originalLabel,
    sampleValues: col.sampleValues,
    valueLabels: col.valueLabels,
    valueMap: col.valueMap,
    disabled: col.disabled,
    disabledReason: col.disabledReason,
  }
}

function singleQuestion(col: SegmentFilterColumnInput, category: string): SegmentFilterQuestion {
  const split = splitStemAndItem(col.originalLabel)
  if (split) {
    return {
      id: `c:${col.sourceColumn}`,
      label: split.stem,
      category,
      kind: 'single',
      options: [toOption(col, split.item)],
    }
  }
  return {
    id: `c:${col.sourceColumn}`,
    label: col.normalizedLabel,
    category,
    kind: 'single',
    options: [toOption(col, col.normalizedLabel)],
  }
}

/**
 * Build the question list from filterable columns.
 *
 * Categorized columns (demographics, geography, …) are always single questions.
 * Uncategorized columns are grouped by shared stem — using the backend Phase-0
 * grouping when available, otherwise the label-based heuristic.
 */
export function buildSegmentFilterQuestions(
  columns: Array<SegmentFilterColumnInput>,
  columnGroups?: Array<AudienceImportSegmentFiltersColumnGroup>,
): Array<SegmentFilterQuestion> {
  const out: Array<SegmentFilterQuestion> = []

  for (const cat of CATEGORY_ORDER) {
    for (const col of columns.filter((c) => c.category === cat)) {
      out.push(singleQuestion(col, cat))
    }
  }

  const uncategorized = columns.filter(
    (c) => !c.category || !CATEGORY_ORDER.includes(c.category),
  )
  if (uncategorized.length === 0) return out

  const columnInputs = uncategorized.map((col) => ({
    label: col.originalLabel,
    key: col.sourceColumn,
  }))
  const stemResult =
    columnGroups && columnGroups.length > 0
      ? buildPhase0Grouping(columnInputs, columnGroups, undefined)
      : groupColumnsBySharedStem(columnInputs)
  const colLookup = new Map(uncategorized.map((col) => [col.sourceColumn, col]))

  for (const entry of stemResult.ordered) {
    if (entry.type === 'group') {
      const options = entry.group.items
        .map((item) => {
          const col = colLookup.get(item.key)
          return col ? toOption(col, item.itemLabel) : null
        })
        .filter((o): o is SegmentFilterOption => o != null)
      if (options.length > 0) {
        out.push({
          id: `g:${entry.group.stem}:${options[0].sourceColumn}`,
          label: entry.group.stem,
          category: 'other',
          kind: 'group',
          options,
        })
      }
    } else if (entry.type === 'single') {
      const col = colLookup.get(entry.key)
      if (col) out.push(singleQuestion(col, 'other'))
    }
  }

  return out
}
